import type { CatalogProduct, SubmissionBatch, SubmissionProgress } from "@nova-ai/shared"
import { formatDateTime } from "../../lib/format-date"
import { humanizeEnumLabel } from "../../lib/enum-label"

export function RunCatalogSummary({ progress }: { progress: SubmissionProgress }) {
  const catalog = progress.catalog
  return (
    <div aria-label="Catalog and image evidence" className="space-y-1 text-sm">
      <p className="text-foreground">
        {catalog
          ? `Catalog: ${catalog.published} published · ${catalog.notPublished} not published · ${catalog.reportedErrors} reported errors · ${catalog.unavailable} unavailable · ${catalog.unobserved} unobserved`
          : "Catalog: unobserved"}
      </p>

      {catalog && (
        <p className="text-muted-foreground">{`${catalog.pending} awaiting reads · ${catalog.stale} stale · ${catalog.stopped} observations stopped · ${catalog.warnings} warnings`}</p>
      )}

      {catalog?.capability === "unsupported" && (
        <p className="text-muted-foreground">
          Catalog observation is unsupported for this marketplace.
        </p>
      )}

      <p className="text-foreground">Images: unverified — unsupported by available API evidence.</p>

      <p className="text-muted-foreground">
        Current catalog state does not prove acceptance of the submitted revision.
      </p>
    </div>
  )
}

export function RunCatalogProducts({ batch }: { batch: SubmissionBatch }) {
  const catalog = batch.catalog
  if (!catalog) {
    return (
      <p>
        {batch.catalogUnavailableReason
          ? `Catalog unavailable: ${batch.catalogUnavailableReason}`
          : "Catalog: unobserved."}{" "}
        Images: unverified — unsupported.
      </p>
    )
  }
  return (
    <details>
      <summary className="cursor-pointer font-medium">
        Catalog observations ({catalog.products.length})
      </summary>

      <p className="mt-2 text-muted-foreground">{`Observation window ends ${formatDateTime(catalog.deadline)}. Enrolled by ${catalog.enrolledBy}.`}</p>

      <ul className="mt-2 space-y-3" aria-label="Catalog products">
        {catalog.products.map((product) => (
          <li key={product.id} className="space-y-1 break-words">
            <p className="font-medium">
              {`${product.sellerId}: ${humanizeEnumLabel(product.publication)}`}
            </p>

            {product.snapshot && (
              <>
                <p>{`Last observed publication: ${product.snapshot.status} · ${formatDateTime(product.snapshot.observedAt)}`}</p>

                <p>{`Observation freshness: ${humanizeEnumLabel(product.freshness)}`}</p>

                <p>{`Expected next observation ${formatDateTime(product.snapshot.freshUntil)}`}</p>

                {product.snapshot.errors.length > 0 && (
                  <pre className="whitespace-pre-wrap break-words">{`Errors: ${JSON.stringify(product.snapshot.errors, null, 2)}`}</pre>
                )}

                {product.snapshot.warnings.length > 0 && (
                  <pre className="whitespace-pre-wrap break-words">{`Warnings: ${JSON.stringify(product.snapshot.warnings, null, 2)}`}</pre>
                )}
              </>
            )}

            {product.lastReadFailure && (
              <p>{`Latest read unavailable: ${product.lastReadFailure.reason} (${formatDateTime(product.lastReadFailure.observedAt)})`}</p>
            )}

            <CatalogFailure label="First failure" failure={product.firstFailure} />

            <CatalogFailure label="Latest failure" failure={product.latestFailure} />

            {product.stopReason ? (
              <p>{`Observation stopped: ${humanizeEnumLabel(product.stopReason)} · ${product.stoppedAt ? formatDateTime(product.stoppedAt) : "Unknown time"}. No continuous monitoring.`}</p>
            ) : (
              <p>{`Next permitted read: ${product.nextReadAt ? formatDateTime(product.nextReadAt) : "No read permitted before window ends"}`}</p>
            )}
          </li>
        ))}
      </ul>
    </details>
  )
}

function CatalogFailure({
  label,
  failure,
}: {
  label: string
  failure: CatalogProduct["firstFailure"]
}) {
  if (!failure) {
    return null
  }
  return (
    <div>
      <p>{`${label}: ${failure.reason} (${formatDateTime(failure.observedAt)})`}</p>

      {failure.errors.length > 0 && (
        <pre className="whitespace-pre-wrap break-words">
          {JSON.stringify(failure.errors, null, 2)}
        </pre>
      )}
    </div>
  )
}
